import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RagDot, StatusBadge, PageHeader } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, LogOut, Shield, Users, CreditCard, LayoutGrid, BookOpen, LibraryBig } from "lucide-react";

const METHODOLOGIES = ["Pmbok", "Agile", "Lean", "SixSigma"];

export default function ProjectsList() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", description: "", primaryMethodology: "Pmbok", plannedStartDate: "", plannedEndDate: "", plannedBudget: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { ...form, plannedBudget: form.plannedBudget ? Number(form.plannedBudget) : null };
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      navigate(`/projects/${data.id}/dashboard`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="Ganttastic" className="h-8 w-8" />
            <div className="font-display font-black tracking-tighter text-lg">Ganttastic<span className="text-[#1E6FE8]">.app</span></div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">{user?.tenantName}</span>
            <span className="font-medium">{user?.name}</span>
            {user?.tenantId && user?.role === "tenant_admin" && (
              <Link data-testid="portfolio-link" to="/portfolio" className="inline-flex items-center gap-1 text-[#1E6FE8] underline"><LayoutGrid className="h-3.5 w-3.5" />Portfolio</Link>
            )}
            {user?.tenantId && (
              <Link data-testid="templates-link" to="/templates" className="inline-flex items-center gap-1 text-[#1E6FE8] underline"><LibraryBig className="h-3.5 w-3.5" />Templates</Link>
            )}
            {user?.tenantId && (
              <Link data-testid="lessons-link" to="/lessons" className="inline-flex items-center gap-1 text-[#1E6FE8] underline"><BookOpen className="h-3.5 w-3.5" />Lessons</Link>
            )}
            {user?.tenantId && (
              <Link data-testid="team-link" to="/team" className="inline-flex items-center gap-1 text-[#1E6FE8] underline"><Users className="h-3.5 w-3.5" />Team</Link>
            )}
            {user?.tenantId && (
              <Link data-testid="billing-link" to="/billing" className="inline-flex items-center gap-1 text-[#1E6FE8] underline"><CreditCard className="h-3.5 w-3.5" />Billing</Link>
            )}
            {user?.role === "platform_admin" && (
              <Link to="/admin" className="inline-flex items-center gap-1 text-[#1E6FE8] underline"><Shield className="h-3.5 w-3.5" />Admin</Link>
            )}
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 hover:text-red-600"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <PageHeader overline={user?.tenantName || "Workspace"} title="Projects"
          actions={<Button data-testid="new-project-btn" onClick={() => setOpen(true)} className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]"><Plus className="h-4 w-4 mr-1" />New Project</Button>} />

        {projects === null ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center text-muted-foreground">No projects yet. Create one to get started.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}/dashboard`} data-testid="project-card"
                className="border border-border bg-card p-5 hover:border-[#1E6FE8] hover:-translate-y-[1px] transition-transform block">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="overline-label">{p.code || "—"}</div>
                    <h3 className="font-display font-bold text-lg tracking-tight leading-snug mt-0.5">{p.name}</h3>
                  </div>
                  <RagDot value={p.overallRag} />
                </div>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.description}</p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <StatusBadge value={p.primaryMethodology === "Pmbok" ? "PMBOK" : p.primaryMethodology} tone="blue" />
                  <StatusBadge value={p.status} />
                  {p.targetGoLiveDate && <span className="text-xs text-muted-foreground">Go-Live: {p.targetGoLiveDate}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New Project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Project name</Label><Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Code</Label><Input data-testid="project-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
              <div>
                <Label>Primary methodology</Label>
                <Select value={form.primaryMethodology} onValueChange={(v) => setForm({ ...form, primaryMethodology: v })}>
                  <SelectTrigger data-testid="project-methodology-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODOLOGIES.map((m) => <SelectItem key={m} value={m}>{m === "Pmbok" ? "PMBOK" : m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Start</Label><Input type="date" value={form.plannedStartDate} onChange={(e) => setForm({ ...form, plannedStartDate: e.target.value })} /></div>
              <div><Label>End</Label><Input type="date" value={form.plannedEndDate} onChange={(e) => setForm({ ...form, plannedEndDate: e.target.value })} /></div>
              <div><Label>Budget</Label><Input type="number" value={form.plannedBudget} onChange={(e) => setForm({ ...form, plannedBudget: e.target.value })} /></div>
            </div>
            <Button data-testid="create-project-submit-btn" onClick={create} disabled={busy || !form.name} className="w-full rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">
              {busy ? "Creating…" : "Create project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
